<script setup lang="ts">
import { Classes, roles, Subjects } from '~/enums'
import { useGamesStore } from '~/stores/games'
import { useCurrentRoleStore } from '~/stores/currentRole'

const gamesStore = useGamesStore()
const currentRoleStore = useCurrentRoleStore()
const { signOut: completeSignOut } = useSignOut()
const { currentStudent, currentRole, currentGrade } =
  storeToRefs(currentRoleStore)
const user = useSupabaseUser()
const drawerNavStudent = [
  {
    id: 'home',
    label: 'Home',
    icon: 'i-heroicons-home-solid',
    color: 'text-purple-700',
    to: '/',
  },
  {
    id: 'classes',
    label: 'Classes',
    icon: 'i-heroicons-academic-cap-solid',
    color: 'text-amber-500',
    to: '/classes',
  },
  {
    id: 'adventure',
    label: 'Adventure',
    icon: 'i-heroicons-map-solid',
    color: 'text-emerald-600',
    to: '/adventure',
  },
]

const drawerNavTeacher = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'i-heroicons-home-solid',
    color: 'text-blue-500',
    to: '/dashboard',
  },
  {
    id: 'classes',
    label: 'Classes',
    icon: 'i-heroicons-academic-cap-solid',
    color: 'text-amber-500',
    to: '/classes',
  },
  {
    id: 'adventure',
    label: 'Adventure',
    icon: 'i-heroicons-map-solid',
    color: 'text-emerald-600',
    to: '/adventure',
  },
]

const drawerNav = computed(() => {
  if (!currentRole.value?.role) return drawerNavStudent
  if (
    currentRole.value.role === roles.ADMIN ||
    currentRole.value.role === roles.TEACHER ||
    currentRole.value.role === roles.SYSTEM_ADMIN
  ) {
    return drawerNavTeacher
  } else {
    return drawerNavStudent
  }
})

const { memberships: schoolRoles, load: loadMemberships } =
  useSchoolMemberships()
const selectedSchool = ref('')
const availableSchoolRoles = ref([] as SchoolRole[])

const { setCurrentRole } = useCurrentRoleStore()

const setUserRole = (schoolRole: SchoolRole) => {
  if (!schoolRole) return
  setCurrentRole(schoolRole)
}

watch(
  () => selectedSchool.value,
  () => {
    availableSchoolRoles.value = schoolRoles.value.filter(
      (schoolRole: SchoolRole) => schoolRole.schoolId === selectedSchool.value,
    )
    if (availableSchoolRoles.value.length === 1) {
      setUserRole(availableSchoolRoles.value[0] ?? ({} as SchoolRole))
    }
  },
)
onMounted(async () => {
  // Load all the games (if already loaded, the store returns)
  const grade = currentGrade.value !== '' ? currentGrade.value : Classes.B1
  gamesStore.initializeGames(grade, Subjects.MATHEMATICS, useGetTerm())

  // Select a school if user is logged in
  // Public auth pages (including invitation acceptance) mount this hidden
  // header too. They must not be redirected before their token is verified.
  if (!user.value) return

  await loadMemberships()
  if (schoolRoles.value.length === 0) {
    return
  }
  if (schoolRoles.value.length === 1) {
    setUserRole(schoolRoles.value[0]!)
    return
  }
  const differentSchools = new Set(
    schoolRoles.value.map((schoolRole: SchoolRole) => schoolRole.schoolId),
  ).size
  if (differentSchools === 1) {
    selectedSchool.value = schoolRoles.value[0]?.schoolId ?? ''
  }
})

const open = ref(false)

defineShortcuts({
  o: () => (open.value = !open.value),
})

const logOut = async () => {
  try {
    await completeSignOut()
  } catch (error) {
    useToast().add({
      title: 'Unable to sign out',
      description: 'Please try again.',
      color: 'error',
    })
    console.error(error)
  }
}

const showDashboardCodeModal = ref(false)
const config = useRuntimeConfig()
</script>

<template>
  <UHeader :toggle="false">
    <template #left>
      <NuxtLink to="/" class="flex items-end text-sm">
        <img src="/wlearn-logo.webp" alt="WLearn" class="h-12 pt-2" />
        <!--<span class="mb-2">v0.15</span>-->
      </NuxtLink>
    </template>

    <template #right>
      <div class="flex items-center justify-end p-4 cursor-pointer">
        <div
          v-if="user?.email"
          class="no-underline hidden sm:flex items-center"
          :class="[currentStudent?.firstName ? 'text-blue-500' : '']"
          @click="navigateTo('/dashboard')"
        >
          <!-- <span
            v-if="user.user_metadata.firstName && user.user_metadata.lastName"
          >
            {{
              user.user_metadata.firstName + ' ' + user.user_metadata?.lastName
            }}
          </span>
          <span v-else>
            {{ user.email || currentRole.role }}
          </span> -->
          <UIcon
            name="i-heroicons-user-circle"
            class="h-6 w-6 lg:ml-4 cursor-pointer"
          />
        </div>
        <NuxtLink
          v-show="!user?.email"
          to="/login"
          class="flex items-center lg:mr-4"
        >
          <UIcon
            name="i-heroicons-user-circle"
            class="h-6 w-6 lg:ml-4 cursor-pointer"
          />
        </NuxtLink>
        <USlideover v-model:open="open">
          <UIcon
            name="i-heroicons-bars-3"
            class="h-8 w-8 ml-4 cursor-pointer"
            @click="open = true"
          />
          <template #header>
            <div class="flex justify-between w-full">
              <NuxtLink to="/">
                <img
                  src="/wlearn-logo.webp"
                  alt="WLearn"
                  class="h-12 pt-2 pl-4"
                />
              </NuxtLink>
              <UButton
                color="primary"
                variant="outline"
                size="xl"
                class="aspect-square cursor-pointer rounded-full px-3"
                @click="open = false"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="size-6"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M6 18 18 6M6 6l12 12"
                  />
                </svg>
              </UButton>
            </div>
          </template>
          <template #body>
            <ul>
              <li
                v-for="(item, index) in drawerNav"
                :key="`nav-item-${index}`"
                class="mt-4"
                :class="item.color"
              >
                <NuxtLink
                  class="flex flex-col justify-center h-12 py-8 hover:bg-blue-600 hover:text-white rounded-l-lg"
                  :to="item.to"
                  @click="
                    item.to === '/dashboard'
                      ? ((showDashboardCodeModal = true), (open = false))
                      : (open = false)
                  "
                >
                  <div class="flex text-2xl">
                    <UIcon :name="item.icon" class="h-8 w-8 mx-4" />
                    <span class="font-bold">
                      {{ item.label }}
                    </span>
                  </div>
                </NuxtLink>
              </li>
            </ul>
          </template>
          <template #footer>
            <div class="w-full">
              <NuxtLink
                v-show="user"
                class="flex flex-col justify-center w-full h-12 py-8 hover:bg-blue-600 hover:text-white cursor-pointer rounded-l-lg"
                @click="logOut"
              >
                <div class="flex text-blue-600 hover:text-white">
                  <UIcon
                    name="i-heroicons-arrow-left-on-rectangle-solid"
                    class="h-8 w-8 mx-4"
                  />
                  <span class="font-bold text-xl"> Sign out </span>
                </div>
              </NuxtLink>
              <NuxtLink
                v-show="!user"
                class="flex flex-col justify-center w-full h-12 py-8 hover:bg-blue-600 hover:text-white cursor-pointer rounded-l-lg"
                @click="(navigateTo('/login'), (open = false))"
              >
                <div class="flex text-blue-600 hover:text-white">
                  <UIcon
                    name="i-heroicons-arrow-right-on-rectangle-solid"
                    class="h-8 w-8 mx-4"
                  />
                  <span class="font-bold text-xl"> Login </span>
                </div>
              </NuxtLink>
              <div class="flex justify-end text-xs text-gray-400">
                Version {{ config.public.appVersion }}.
              </div>
            </div>
          </template>
        </USlideover>
      </div>
    </template>
  </UHeader>
</template>
